import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';

const articleLinks = articles.map((a) => ({
  label: a.title,
  to: `/article/${a.slug}`,
}));

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (guideRef.current && !guideRef.current.contains(event.target as Node)) {
        setGuideOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-background-200/60">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-14 md:h-16">
          <Link to="/" className="flex items-center gap-2 group" onClick={() => setMobileOpen(false)}>
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-database-2-line text-base md:text-lg text-white"></i>
            </div>
            <span className="font-heading font-semibold text-foreground-900 text-sm md:text-base whitespace-nowrap">
              Sugargoo <span className="text-primary-600">Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap font-medium">
              Home
            </Link>

            <div className="relative" ref={guideRef}>
              <button
                onClick={() => setGuideOpen(!guideOpen)}
                className="flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-600 transition-colors whitespace-nowrap font-medium"
              >
                Shopping Guide <i className={`ri-arrow-down-s-line text-xs transition-transform ${guideOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {guideOpen && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 rounded-xl border border-background-200 shadow-lg overflow-hidden z-50 max-h-96 overflow-y-auto">
                  <div className="p-2">
                    <p className="text-xs text-foreground-400 uppercase tracking-wider font-semibold px-3 py-1.5">Articles & Guides</p>
                    {articleLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => setGuideOpen(false)}
                        className="block px-3 py-2 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm px-4 py-2 rounded-full bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-lg text-foreground-700 ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 flex flex-col gap-1 border-t border-background-200/60 pt-3 max-h-[70vh] overflow-y-auto">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 text-sm text-foreground-700 hover:text-primary-600 transition-colors py-2.5 px-2 rounded-lg hover:bg-background-100 font-medium"
            >
              <i className="ri-home-4-line text-base w-5 text-center"></i> Home
            </Link>

            <button
              onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
              className="flex items-center justify-between w-full text-sm text-foreground-700 hover:text-primary-600 transition-colors py-2.5 px-2 rounded-lg hover:bg-background-100 font-medium"
            >
              <span className="flex items-center gap-2">
                <i className="ri-book-open-line text-base w-5 text-center"></i> Shopping Guide
              </span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${mobileGuideOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileGuideOpen && (
              <div className="ml-7 border-l-2 border-background-200 pl-3 space-y-0.5">
                {articleLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => { setMobileOpen(false); setMobileGuideOpen(false); }}
                    className="block text-xs text-foreground-600 hover:text-primary-600 transition-colors py-2 px-2 rounded-md hover:bg-background-100"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}

            <div className="border-t border-background-200 my-1"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 text-sm px-4 py-2.5 rounded-full bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors text-center justify-center whitespace-nowrap mt-1"
            >
              <i className="ri-shopping-cart-2-line text-base"></i> Buy It Now
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}